/**
 * ThemeProvider
 *
 * The main Next.js theme provider. Adds Next.js-specific concerns on top of
 * BrandedChakraProvider (which handles the portable theming logic):
 *
 * 1. **ColorModeProvider** - Dark/light mode via next-themes
 * 2. **BrandedChakraProvider** - Chakra system, font CSS vars, Google Font loading
 * 3. **URL query param fallback** - `?theme-primary=#FF0000`
 * 4. **Toaster** - Global toast notification system
 *
 * ## Architecture
 *
 * ```
 * PageThemeConfigProvider (app-level context for page colors)
 *   └── ThemeProvider
 *         └── ColorModeProvider (next-themes)
 *               └── BrandedChakraProvider (portable - shared with website editor iframe)
 *                     └── App + Toaster
 * ```
 *
 * BrandedChakraProvider is the single source of truth for:
 * - generateTheme() with branding colors → ChakraProvider(system)
 * - generateScopedFontVars() → font CSS variable overrides
 * - Google Font <link> tags → loads font files into whichever document it's mounted in
 *
 * This same provider is used inside the Puck iframe via BrandedIframeWrapper,
 * ensuring identical theming between the editor preview and the public site.
 */
import React, { useMemo } from "react";
/* eslint-disable-next-line no-restricted-imports */
import { Toaster, Portal } from "@chakra-ui/react";
import { useRouter } from "next/router";
import { getActiveToaster } from "@ui-kit/feedback/alerts/Toast/Toast.web";
import ToastAlert from "@ui-kit/feedback/alerts/ToastAlert/ToastAlert.web";
import { usePageThemeConfig } from "@shared/contexts/PageThemeConfigContext/PageThemeConfigContext";
import { validateColorString } from "@shared/theme/utils/theme-generator";
import { BrandedChakraProvider } from "@shared/modules/branding/components/BrandedChakraProvider";
import { useMobileWebviewContext } from "@shared/contexts/MobileWebviewContext/MobileWebviewContext";
import type { LayoutThemeConfig } from "@shared/config/layout.config";
import type { ThemeConfig as WebsiteThemeConfig } from "@app-contexts/dashboard/features/websites/data/types/website";
import { ColorModeProvider } from "@/providers/ColorModeProvider";

type ThemeProviderProps = {
  children: React.ReactNode;
  /**
   * Force a specific color mode. Passed to ColorModeProvider.
   * Will be controlled by backend config in the future.
   */
  forcedColorMode?: "light" | "dark";
  /**
   * CSP nonce for inline script injection.
   * Required for Content Security Policy compliance.
   */
  nonce?: string;
  websiteThemeConfig?: WebsiteThemeConfig | null;
};

const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  forcedColorMode,
  nonce,
  websiteThemeConfig,
}) => {
  const router = useRouter();
  const pageThemeConfig = usePageThemeConfig();

  // URL query params as fallback for colors
  const urlPrimary = validateColorString(router?.query?.["theme-primary"]);
  const urlSecondary = validateColorString(router?.query?.["theme-secondary"]);

  // Merge page config with URL fallbacks into a single themeConfig
  // Page config takes precedence over URL params
  const mergedThemeConfig = useMemo((): LayoutThemeConfig | undefined => {
    const primaryColor =
      validateColorString(pageThemeConfig?.primaryColor) || urlPrimary;
    const secondaryColor =
      validateColorString(pageThemeConfig?.secondaryColor) || urlSecondary;

    // If no config at all, return undefined (BrandedChakraProvider uses defaults)
    if (!pageThemeConfig && !primaryColor && !secondaryColor) {
      return undefined;
    }

    return {
      ...pageThemeConfig,
      colorMode: pageThemeConfig?.colorMode ?? "light",
      primaryColor: primaryColor ?? undefined,
      secondaryColor: secondaryColor ?? undefined,
    };
  }, [pageThemeConfig, urlPrimary, urlSecondary]);

  const { webviewValueAdapter } = useMobileWebviewContext();

  const activeToaster = useMemo(() => {
    return {
      toaster: getActiveToaster(),
      key: webviewValueAdapter({ web: "web", webview: "webview" }),
    };
  }, [webviewValueAdapter]);

  const effectiveForcedMode = pageThemeConfig?.forceColorMode
    ? pageThemeConfig.colorMode
    : forcedColorMode;

  return (
    <ColorModeProvider forcedMode={effectiveForcedMode} nonce={nonce}>
      <BrandedChakraProvider
        themeConfig={mergedThemeConfig}
        websiteThemeConfig={websiteThemeConfig ?? undefined}
      >
        {children}
        <Portal>
          <Toaster
            toaster={activeToaster.toaster}
            key={activeToaster.key}
            insetInline={{ mdDown: "4" }}
          >
            {(toast) => (
              <ToastAlert
                status={toast.type === "loading" ? "info" : toast.type}
                title={toast.title as string}
                description={toast.description as string}
              />
            )}
          </Toaster>
        </Portal>
      </BrandedChakraProvider>
    </ColorModeProvider>
  );
};

export default ThemeProvider;
